import React from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { addYears, formatISO } from 'date-fns';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import InfoIcon from '@mui/icons-material/Info';
import { DrawerLayoutWrapper } from 'common';
import ProgressBar from 'feature/dispatching/components/ProgressBar';
import WizardStatus from 'feature/dispatching/components/WizardStatus';
import AccordionSummary from 'feature/dispatching/components/AccordionSummary';
import DocumentListItem from '../components/DocumentListItem';
import TrailerDetails from '../components/TrailerDetails';
import TrailerUpdates from '../components/TrailerUpdates';
import { useCreateTrailerMutation } from '../../trailerStatusEndpoints';

const accordionStyles = {
  border: 1,
  borderColor: 'graphic.light',
  boxShadow: 'none',
};
const accordionDetailsStyles = {
  backgroundColor: 'surface.white',
  p: 0,
  pl: 1,
  pr: 1.5,
};

const Summary = ({ onClose, prev, reset, trailerState }) => {
  const {
    vaporTightnessCode,
    effectiveDate,
    compartments,
    locations,
    hasMexicanTrailerNumber,
    mexicanTrailerNumber,
  } = trailerState;
  const [submit, { data, error, isError, isLoading, isSuccess }] = useCreateTrailerMutation();

  const handleSave = () => {
    const sanitizedState = R.omit(['hasMexicanTrailerNumber'], trailerState);
    const transformations = {
      locations: R.map(R.prop('locationId')),
    };
    const updateTrailerState = {
      ...sanitizedState,
      compartments: sanitizedState.compartments.map(compartment => {
        return {
          ...compartment,
          capacityInGallons: parseInt(compartment.capacityInGallons.replace(/,/g, '')),
        };
      }),
    };
    const body = R.evolve(transformations, updateTrailerState);
    submit({ body });
  };

  const expirationDate = formatISO(addYears(new Date(effectiveDate), 1));

  const buttons = isSuccess ? (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: 1 }}>
      <Button onClick={reset} sx={{ width: 150 }} variant="outlined">
        Start Another
      </Button>
      <Button onClick={onClose} sx={{ width: 150 }}>
        Done
      </Button>
    </Box>
  ) : (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: 1 }}>
      <Button onClick={prev} sx={{ width: 150 }} variant="outlined">
        Back
      </Button>
      <Button onClick={handleSave} sx={{ width: 150 }}>
        Submit
      </Button>
    </Box>
  );

  return (
    <DrawerLayoutWrapper
      buttons={buttons}
      headerButtonText="CLOSE"
      isLoading={isLoading}
      onHeaderButtonClick={onClose}
      title="New Trailer"
    >
      <ProgressBar
        label="Summary (5 of 5)"
        sx={{
          '&.MuiLinearProgress-colorPrimary > .MuiLinearProgress-bar': {
            borderRadius: '10px',
          },
        }}
        value={100}
      />
      <WizardStatus
        error={error}
        isError={isError}
        isSuccess={isSuccess}
        successMessage="New Trailer created and successfully set for the below trailer and locations."
      />
      <TrailerDetails
        effectiveDate={effectiveDate}
        expirationDate={expirationDate}
        hasMexicanTrailerNumber={hasMexicanTrailerNumber}
        isNew
        locations={locations}
        mexicanTrailerNumber={mexicanTrailerNumber}
        vaporTightnessCode={vaporTightnessCode}
      />
      <Accordion sx={accordionStyles}>
        <AccordionSummary count={2} label="Documents" />
        <AccordionDetails sx={accordionDetailsStyles}>
          <DocumentListItem title="Wet Test" trailerId={data?.trailerId} type="WT" />
          <Divider />
          <DocumentListItem title="Method27" trailerId={data?.trailerId} type="M27" />
        </AccordionDetails>
      </Accordion>
      <Accordion sx={accordionStyles}>
        <AccordionSummary count={compartments.length} label="Trailer Compartments" />
        <AccordionDetails sx={accordionDetailsStyles}>
          {compartments.map(({ capacityInGallons, position }) => (
            <ListItem key={position}>
              <ListItemText
                primary={`Compartment ${position}`}
                secondary={`${capacityInGallons} gal`}
                slotProps={{
                  primary: { variant: 'caption' },
                  secondary: { fontWeight: '700', variant: 'subtitle2' },
                }}
              />
            </ListItem>
          ))}
        </AccordionDetails>
      </Accordion>
      <Accordion sx={accordionStyles}>
        <AccordionSummary count={locations.length} label="Locations" />
        <AccordionDetails sx={accordionDetailsStyles}>
          {locations.map(({ name, locationId }) => (
            <ListItem dense key={locationId}>
              <ListItemText
                primary={`${locationId} ${name}`}
                slotProps={{
                  primary: { fontWeight: '700', variant: 'subtitle2' },
                }}
              />
            </ListItem>
          ))}
        </AccordionDetails>
      </Accordion>
      {isSuccess && <TrailerUpdates updates={data.processingHistory} />}
      <Box sx={{ mt: 2.5, pb: 3, px: 3 }}>
        <Alert icon={<InfoIcon fontSize="inherit" />} severity="info" variant="trailerWizard">
          It can take up to 48 hours until a Trailer can begin loading at a location.
        </Alert>
      </Box>
    </DrawerLayoutWrapper>
  );
};

Summary.propTypes = {
  onClose: PropTypes.func.isRequired,
  prev: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  trailerState: PropTypes.shape({
    compartments: PropTypes.arrayOf(PropTypes.shape({})),
    effectiveDate: PropTypes.string,
    hasMexicanTrailerNumber: PropTypes.bool,
    locations: PropTypes.arrayOf(PropTypes.shape({})),
    mexicanTrailerNumber: PropTypes.string,
    vaporTightnessCode: PropTypes.string,
  }).isRequired,
};

export default Summary;
